import React, { useEffect, useRef, useCallback, useState } from "react";
import PropTypes from "prop-types";

// Assuming tui is loaded globally via script tags in the layout
// const TuiImageEditor = window.tui ? window.tui.ImageEditor : null; // Will access directly in initEditor

const ListingPhotoEditor = ({ isOpen, photo, onSave, onClose }) => {
  console.log("[ListingPhotoEditor] Props received:", { isOpen, photo }); // Log props
  const editorInstance = useRef(null);
  const editorWrapperRef = useRef(null); // Ref for the TUI editor container div
  const [isLoading, setIsLoading] = useState(false);

  const whiteTheme = () => ({
    "common.bi.image":
      "https://uicdn.toast.com/toastui/img/tui-image-editor-bi.png",
    "common.bisize.width": "251px",
    "common.bisize.height": "21px",
    "common.backgroundImage": "none",
    "common.backgroundColor": "#fff",
    "common.border": "1px solid #c1c1c1",
    "header.backgroundImage": "none",
    "header.backgroundColor": "transparent",
    "header.border": "0px",
    "loadButton.display": "none", // Ensure these are not hiding critical parts
    "downloadButton.display": "none", // Ensure these are not hiding critical parts
    "menu.normalIcon.color": "#8a8a8a",
    "menu.activeIcon.color": "#555555",
    "menu.disabledIcon.color": "#434343",
    "menu.hoverIcon.color": "#e9e9e9",
    "menu.iconSize.width": "24px",
    "menu.iconSize.height": "24px",
    "submenu.normalIcon.color": "#8a8a8a",
    "submenu.activeIcon.color": "#555555",
    "submenu.iconSize.width": "32px",
    "submenu.iconSize.height": "32px",
    "submenu.backgroundColor": "transparent",
    "submenu.partition.color": "#e5e5e5",
    "submenu.normalLabel.color": "#858585",
    "submenu.normalLabel.fontWeight": "normal",
    "submenu.activeLabel.color": "#000",
    "submenu.activeLabel.fontWeight": "normal",
    "checkbox.border": "1px solid #ccc",
    "checkbox.backgroundColor": "#fff",
    "range.pointer.color": "#333",
    "range.bar.color": "#ccc",
    "range.subbar.color": "#606060",
    "range.disabledPointer.color": "#d3d3d3",
    "range.disabledBar.color": "rgba(85,85,85,0.06)",
    "range.disabledSubbar.color": "rgba(51,51,51,0.2)",
    "range.value.color": "#000",
    "range.value.fontWeight": "normal",
    "range.value.fontSize": "11px",
    "range.value.border": "0",
    "range.value.backgroundColor": "#f5f5f5",
    "range.title.color": "#000",
    "range.title.fontWeight": "lighter",
    "colorpicker.button.border": "0px",
    "colorpicker.title.color": "#000",
  });

  const initEditor = useCallback(
    (imageSrc) => {
      const Editor =
        window.tui && window.tui.ImageEditor ? window.tui.ImageEditor : null;
      if (!Editor) {
        console.error(
          "Toast UI Image Editor (window.tui.ImageEditor) not found at the time of initEditor call."
        );
        return;
      }
      if (editorInstance.current) {
        editorInstance.current.destroy();
        editorInstance.current = null;
      }
      if (!editorWrapperRef.current) {
        console.error("Editor wrapper ref not found");
        return;
      }

      const editor = new Editor(editorWrapperRef.current, {
        includeUI: {
          loadImage: {
            path: imageSrc,
            name: photo?.clientId || "current-image",
          },
          theme: whiteTheme(),
          uiSize: { height: "600px" }, // Adjust as needed
          menuBarPosition: "bottom", // Or 'top', 'left', 'right'
          menu: [
            "crop",
            "flip",
            "rotate",
            // "draw",
            "shape",
            // "icon",
            "text",
            // "mask",
            // "filter",
          ],
        },
        cssMaxWidth: 700, // Adjust as needed
        cssMaxHeight: 500, // Adjust as needed
        usageStatistics: false,
      });
      editorInstance.current = editor;
    },
    [photo] // Dependency on photo to re-init if different photo is passed
  );

  useEffect(() => {
    let imageToLoad = null;

    if (isOpen && photo) {
      if (photo.imageUrlForFrontend) {
        console.log("photo.imageUrlForFrontend", photo);
        imageToLoad = `/image-proxy?url=${encodeURIComponent(
          photo.imageUrlForFrontend
        )}`;
      } else if (photo.preview) {
        imageToLoad = photo.preview;
      }
    }

    if (imageToLoad) {
      setIsLoading(true);
      const img = new Image();
      img.onload = () => {
        initEditor(imageToLoad);
        setIsLoading(false);
      };
      img.onerror = () => {
        console.error("Error loading image for editor");
        setIsLoading(false);
        // Optionally, handle the error in the UI
      };
      img.src = imageToLoad;
    }

    return () => {
      if (editorInstance.current) {
        editorInstance.current.destroy();
        editorInstance.current = null;
      }
    };
  }, [isOpen, photo, initEditor]);

  const handleSave = () => {
    if (editorInstance.current) {
      const dataURL = editorInstance.current.toDataURL();
      onSave(photo.id || photo.clientId, dataURL); // Pass original ID/clientId and new dataURL
    }
  };

  if (!isOpen) {
    console.log(
      "[ListingPhotoEditor] Render: isOpen is false, returning null."
    );
    return null;
  }

  console.log(
    "[ListingPhotoEditor] Render: isOpen is true, attempting to render modal."
  );

  return (
    <div
      className="listing-photo-editor-modal"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px", // Slightly more rounded
          width: "80%",
          maxWidth: "900px",
          position: "relative", // Needed for absolute positioning of the close button
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "#888",
            lineHeight: 1,
          }}
        >
          &times;
        </button>
        <h3 style={{ marginRight: "30px" }}>Edit Image</h3>
        <div style={{ height: "600px", width: "100%", position: "relative" }}>
          {isLoading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <span>Loading...</span>
            </div>
          )}
          <div
            ref={editorWrapperRef}
            style={{ height: "600px", width: "100%" }}
          ></div>
        </div>
        <div style={{ marginTop: "20px", textAlign: "right" }}>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              cursor: "pointer",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontWeight: "bold",
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

ListingPhotoEditor.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  photo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    clientId: PropTypes.string, // Added clientId
    imageUrlForFrontend: PropTypes.string,
    preview: PropTypes.string,
    // other photo props if needed by editor setup (e.g. name)
  }),
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ListingPhotoEditor;
